import React, { useState, useEffect, useRef, useCallback } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import { getFirestore, collection, onSnapshot, addDoc, doc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore'
import { useAuth } from '../contexts/AuthContext'
import CustomerList from './CustomerList'

// Simple Rich Text Editor Component
const SimpleRichTextEditor = ({ content, onChange, placeholder, isExpanded = false, onExpand }) => {
  const editorRef = useRef(null);
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef(null);

  // Convert stored content (newline-separated) to HTML for editing
  const convertToEditorFormat = (storedContent) => {
    if (!storedContent) return '';
    return storedContent
      .split('\n')
      .map(line => line.trim() ? `<p>${line}</p>` : '<br/>')
      .join('');
  };

  // Convert editor HTML back to newline-separated format for storage
  const convertFromEditorFormat = (editorHTML) => {
    if (!editorHTML) return '';
    
    // Create a temporary div to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = editorHTML;
    
    // Extract text content while preserving line breaks
    const lines = [];
    const children = tempDiv.children;
    
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (child.tagName === 'P') {
        lines.push(child.textContent || child.innerText || '');
      } else if (child.tagName === 'BR') {
        lines.push('');
      } else if (child.tagName === 'UL' || child.tagName === 'OL') {
        // Handle lists
        const listItems = child.querySelectorAll('li');
        listItems.forEach(li => {
          const prefix = child.tagName === 'UL' ? '• ' : '1. ';
          lines.push(prefix + (li.textContent || li.innerText || ''));
        });
      } else {
        // For other elements, just get text content
        lines.push(child.textContent || child.innerText || '');
      }
    }
    
    return lines.join('\n');
  };

  // Apply text formatting
  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      const editorHTML = editorRef.current.innerHTML;
      const convertedContent = convertFromEditorFormat(editorHTML);
      onChange(convertedContent);
    }
  };

  // Handle content changes
  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      // Mark as typing and reset timeout
      setIsTyping(true);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      const editorHTML = editorRef.current.innerHTML;
      const convertedContent = convertFromEditorFormat(editorHTML);
      onChange(convertedContent);
      
      // Clear typing state after a short delay
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
      }, 500);
    }
  }, [onChange]);

  // Handle Enter key to create proper paragraph breaks
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      document.execCommand('insertHTML', false, '</p><p>');
    }
  };

  // Update editor content when content prop changes
  useEffect(() => {
    if (editorRef.current && !isTyping) {
      const editorHTML = convertToEditorFormat(content);
      
      // Only update if the content is actually different and the editor is not focused
      // This prevents cursor jumping during typing
      if (editorRef.current.innerHTML !== editorHTML && document.activeElement !== editorRef.current) {
        editorRef.current.innerHTML = editorHTML;
      }
    }
  }, [content, isTyping]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="border border-gray-200 dark:border-[#2A2A2A] rounded-2xl overflow-hidden bg-gray-50 dark:bg-[#111111]">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-3 bg-white dark:bg-black border-b border-gray-200 dark:border-[#2A2A2A]">
        {/* Text Formatting */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => applyFormat('bold')}
            className="px-3 py-1.5 text-sm font-bold bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
            title="Bold"
          >
            B
          </button>
          <button
            type="button"
            onClick={() => applyFormat('italic')}
            className="px-3 py-1.5 text-sm italic bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
            title="Italic"
          >
            I
          </button>
          <button
            type="button"
            onClick={() => applyFormat('underline')}
            className="px-3 py-1.5 text-sm underline bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
            title="Underline"
          >
            U
          </button>
        </div>

        <div className="w-px h-4 bg-gray-300 dark:bg-[#666666]"></div>

        {/* Lists */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => applyFormat('insertUnorderedList')}
            className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
            title="Bullet List"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => applyFormat('insertOrderedList')}
            className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
            title="Numbered List"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 4V2a1 1 0 011-1h2a1 1 0 011 1v2M7 4H5a2 2 0 00-2 2v6a2 2 0 002 2h8a2 2 0 002-2V6a2 2 0 00-2-2h-2M7 4v2a1 1 0 001 1h2a1 1 0 001-1V4" />
            </svg>
          </button>
        </div>

        {/* Word Count */}
        <div className="ml-auto text-xs text-gray-500 dark:text-[#666666]">
          {content ? content.replace(/\n/g, ' ').length : 0} characters
        </div>

        {/* Expand Button - Only show when not expanded and onExpand is provided */}
        {!isExpanded && onExpand && (
          <>
            <div className="w-px h-4 bg-gray-300 dark:bg-[#666666]"></div>
            <button
              type="button"
              onClick={onExpand}
              className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-[#111111] hover:bg-gray-100 dark:hover:bg-[#1A1A1A] border border-gray-200 dark:border-[#2A2A2A] rounded transition-colors text-gray-900 dark:text-white"
              title="Expand Editor"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4a1 1 0 011-1h4M4 16v4a1 1 0 001 1h4m8-16h4a1 1 0 011 1v4m-4 12h4a1 1 0 001-1v-4" />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* Editor Content */}
      <div 
        ref={editorRef}
        contentEditable
        onInput={handleContentChange}
        onKeyDown={handleKeyDown}
        className="p-4 bg-gray-50 dark:bg-[#111111] text-gray-900 dark:text-white focus:outline-none prose prose-sm dark:prose-invert max-w-none overflow-y-auto"
        style={{
          minHeight: isExpanded ? '400px' : '200px',
          maxHeight: isExpanded ? '60vh' : '300px',
          lineHeight: '1.6'
        }}
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />
      
      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9CA3AF;
          font-style: italic;
          pointer-events: none;
        }
        .dark [contenteditable]:empty:before {
          color: #666666;
        }
        [contenteditable]:empty:focus:before {
          content: '';
        }
        /* Match Agreement.js display formatting */
        .prose p { 
          margin: 0 0 1.5rem 0; /* mb-6 equivalent */
          line-height: 1.6;
        }
        .prose ul, .prose ol { 
          margin: 0 0 1.5rem 0; 
          padding-left: 1.5rem; 
        }
        .prose li { 
          margin: 0.25rem 0; 
        }
        .prose strong { 
          font-weight: 600; 
        }
        .prose em { 
          font-style: italic; 
        }
        /* Ensure text color in contenteditable */
        [contenteditable] {
          color: inherit;
        }
        .dark [contenteditable] {
          color: #fff;
        }
        [contenteditable] * {
          color: inherit;
        }
        /* Ensure proper paragraph spacing matches display */
        [contenteditable] p:last-child {
          margin-bottom: 0;
        }
      `}</style>
    </div>
  );
};

export default function AgreementModal({ isOpen, onClose, project, client, email }) {
  const { currentUser } = useAuth()
  const navigate = useNavigate()

  // Add state for client dropdown
  const [showClientDropdown, setShowClientDropdown] = useState(false)
  const [customers, setCustomers] = useState([])
  const clientDropdownRef = useRef(null)
  
  // Add new client form state and debounce ref
  const [newClientEmail, setNewClientEmail] = useState('')
  const [showNewClientForm, setShowNewClientForm] = useState(false)
  const newClientDebounceRef = useRef(null)

  // Add expanded editor state
  const [showExpandedEditor, setShowExpandedEditor] = useState(false)
  
  // Client editing state
  const [isEditingClient, setIsEditingClient] = useState(false)
  const [clientSaved, setClientSaved] = useState(false)
  const [clientCompleted, setClientCompleted] = useState(false) // Track when customer selection is complete

  // Helper function to extract email address
  const getEmailFromString = (str) => {
    if (!str) return '';
    // Try to extract from angle brackets first
    const angleEmailMatch = str.match(/<([^>]+)>/);
    if (angleEmailMatch) {
      return angleEmailMatch[1];
    }
    // If no angle brackets, try to find a plain email
    const plainEmailMatch = str.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/);
    if (plainEmailMatch) {
      return plainEmailMatch[1];
    }
    return str;
  };

  // Helper function to extract name from email sender string
  // Format: "Name <email@example.com>" or just "email@example.com"
  const getNameFromSender = (str) => {
    if (!str) return '';
    // Check for format: "Name <email@example.com>"
    const nameMatch = str.match(/^([^<]+)</);
    if (nameMatch) {
      return nameMatch[1].trim();
    }
    // Check for format: "Name" email@example.com (no angle brackets but has a name before email)
    const parts = str.split(/\s+/);
    if (parts.length > 1) {
      // Check if last part is an email
      const lastPart = parts[parts.length - 1];
      if (lastPart.includes('@')) {
        return parts.slice(0, -1).join(' ').trim();
      }
    }
    // If it's just an email address, return empty (we'll use email as fallback elsewhere)
    if (str.includes('@') && !str.includes(' ')) {
      return '';
    }
    return str.trim();
  };

  // Helper function to get first name from full name
  const getFirstName = (fullName) => {
    if (!fullName) return '';
    return fullName.split(' ')[0];
  };

  // Initialize form with project, client, or email data if available
  const [form, setForm] = useState(() => {
    const extractedEmail = getEmailFromString(email?.sender);
    const extractedName = getNameFromSender(email?.sender);
    // Use extracted name if available, otherwise fall back to email as display name
    const clientName = client?.name || extractedName || extractedEmail || '';
    const clientEmailAddr = client?.email || extractedEmail || '';
    // Generate title using first name
    const firstName = getFirstName(clientName);
    const autoTitle = firstName ? `Agreement with ${firstName}` : '';
    
    return {
      client: clientName,
      scope: project?.projectName || email?.subject || '',
      price: project?.price || '',
      currency: project?.currency || 'USD',
      timeline: project?.dueDate ? project.dueDate.toDate().toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      projectId: project?.id || null,
      clientId: client?.id || project?.clientId || null,
      clientEmail: clientEmailAddr,
      recipients: clientEmailAddr ? [{ email: clientEmailAddr, id: Date.now() }] : [], // Multiple recipients array
      mode: 'ai', // 'ai' or 'manual'
      manualText: '',
      title: autoTitle // Auto-fill title with "Agreement with [First Name]"
    };
  })

  // Update form when project, client, or email changes
  useEffect(() => {
    if (project || client || email) {
      const extractedEmail = getEmailFromString(email?.sender);
      const extractedName = getNameFromSender(email?.sender);
      // Use extracted name if available, otherwise fall back to email as display name
      const clientName = client?.name || extractedName || extractedEmail || '';
      const clientEmailAddr = client?.email || extractedEmail || '';
      // Generate title using first name
      const firstName = getFirstName(clientName);
      const autoTitle = firstName ? `Agreement with ${firstName}` : '';
      
      setForm(prev => ({
        ...prev,
        client: clientName || prev.client,
        scope: project?.projectName || email?.subject || prev.scope,
        price: project?.price || prev.price,
        currency: project?.currency || prev.currency,
        timeline: project?.dueDate ? project.dueDate.toDate().toISOString().split('T')[0] : prev.timeline || new Date().toISOString().split('T')[0],
        projectId: project?.id || prev.projectId,
        clientId: client?.id || project?.clientId || prev.clientId,
        clientEmail: clientEmailAddr || prev.clientEmail,
        title: autoTitle || prev.title // Auto-fill title if not already set
      }));
    }
  }, [project, client, email]);

  // Load the user's clients from Firestore
  useEffect(() => {
    if (!currentUser) return;
    
    const firestore = getFirestore();
    const clientsRef = collection(firestore, 'users', currentUser.uid, 'clients');
    const unsubscribe = onSnapshot(clientsRef, snapshot => {
      const clientsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setCustomers(clientsData);
    });
    
    return () => unsubscribe();
  }, [currentUser]);

  // Add click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showClientDropdown && clientDropdownRef.current && !clientDropdownRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
    };

    if (showClientDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClientDropdown]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (newClientDebounceRef.current) {
        clearTimeout(newClientDebounceRef.current);
      }
    };
  }, []);

  const [isGeneratingAI, setIsGeneratingAI] = useState(false)

  // Recipient management functions
  const addRecipient = () => {
    if (form.recipients.length >= 5) return; // Max 5 recipients
    
    setForm(prev => ({
      ...prev,
      recipients: [...prev.recipients, { email: '', id: Date.now() }]
    }));
  };

  const updateRecipient = (id, email) => {
    setForm(prev => ({
      ...prev,
      recipients: prev.recipients.map(r => 
        r.id === id ? { ...r, email } : r
      )
    }));
  };

  const removeRecipient = (id) => {
    setForm(prev => ({
      ...prev,
      recipients: prev.recipients.filter(r => r.id !== id)
    }));
  };

  if (!isOpen) return null

  const handleChange = (e) => {
    const { name, value } = e.target
    
    // Special handling for client field
    if (name === 'client') {
      // Always update the field value first
      setForm(prev => ({ ...prev, [name]: value, clientId: null }));
      
      // Reset client completion when user starts typing a new name
      if (clientCompleted) {
        setClientCompleted(false);
        setClientSaved(false);
      }
      
      // Clear any existing debounce
      if (newClientDebounceRef.current) {
        clearTimeout(newClientDebounceRef.current);
      }
      
      // Check if the value contains an email in angle brackets
      const emailMatch = value.match(/<([^>]+)>/);
      if (emailMatch) {
        // Extract the email from between angle brackets
        const email = emailMatch[1];
        // Extract the name part (everything before the <)
        const namePart = value.split('<')[0].trim();
        
        // Update both client name and email fields
        setForm(prev => ({ 
          ...prev, 
          [name]: namePart, 
          clientEmail: email
        }));
        setShowClientDropdown(false);
        setShowNewClientForm(false);
        return;
      }
      
      // Check if this client name matches any existing clients exactly
      const exactMatch = customers.find(c => 
        c.name.toLowerCase() === value.toLowerCase()
      );
      
      if (exactMatch) {
        // Auto-populate with existing client data
        setForm(prev => ({
          ...prev,
          client: exactMatch.name,
          clientEmail: exactMatch.email,
          clientId: exactMatch.id
        }));
        setShowClientDropdown(false);
        setShowNewClientForm(false);
        return;
      }
      
      // Check if there are any partial matches
      const partialMatches = customers.filter(c => 
        c.name.toLowerCase().includes(value.toLowerCase()) ||
        c.email.toLowerCase().includes(value.toLowerCase()) ||
        (c.company && c.company.toLowerCase().includes(value.toLowerCase()))
      );
      
      if (value.trim() && partialMatches.length > 0) {
        // Show dropdown with filtered results
        setShowClientDropdown(true);
        setShowNewClientForm(false);
      } else if (!value.trim()) {
        // Empty field - hide everything
        setShowClientDropdown(false);
        setShowNewClientForm(false);
      } else if (value.trim()) {
        // No matches found - this is a new client, show email form after debounce
        // Debounce the new client form to avoid showing it while user is still typing
        newClientDebounceRef.current = setTimeout(() => {
          setShowClientDropdown(false);
          setShowNewClientForm(true);
          setNewClientEmail('');
        }, 800); // Wait 800ms after user stops typing
      }
      return;
    }
    
    // Default handling for other fields
    setForm(prev => ({ ...prev, [name]: value }))
  }

  // Handle client selection from dropdown
  const handleCustomerSelect = (customer) => {
    setForm(prev => ({
      ...prev,
      client: customer.name,
      clientEmail: customer.email,
      recipients: customer.email ? [{ email: customer.email, id: Date.now() }] : prev.recipients, // Update recipients
      clientId: customer.id
    }));
    setClientCompleted(true); // Mark customer selection as complete
    setClientSaved(true);
    // Auto-hide the "Saved" indicator after 3 seconds
    setTimeout(() => setClientSaved(false), 3000);
    setShowClientDropdown(false);
  };

  // Handle new client email addition
  const handleAddNewClient = async () => {
    if (!form.client.trim()) return;
    
    const clientData = {
      name: form.client,
      email: newClientEmail,
      createdAt: new Date()
    };
    
    try {
      const firestore = getFirestore();
      const clientsRef = collection(firestore, 'users', currentUser.uid, 'clients');
      const newClientDoc = await addDoc(clientsRef, clientData);
      
      // Update form data with new client
      setForm(prev => ({
        ...prev,
        clientEmail: newClientEmail,
        recipients: newClientEmail ? [{ email: newClientEmail, id: Date.now() }] : prev.recipients, // Update recipients
        clientId: newClientDoc.id
      }));
      
      setShowNewClientForm(false);
      setNewClientEmail('');
      setClientCompleted(true); // Mark customer addition as complete
      setClientSaved(true);
      // Auto-hide the "Saved" indicator after 3 seconds
      setTimeout(() => setClientSaved(false), 3000);
      toast.success(`${form.client} added to your contacts!`);
    } catch (error) {
      console.error('Error adding client:', error);
      toast.error('Failed to add client');
    }
  };

  // Skip adding new client and just use email for agreement
  const handleSkipAddingClient = () => {
    setForm(prev => ({
      ...prev,
      clientEmail: newClientEmail
    }));
    setShowNewClientForm(false);
    setNewClientEmail('');
  };

  // AI Generation - simplified to go directly to Agreement page
  const handleGenerateAI = async () => {
    setIsGeneratingAI(true)
    try {
      const payload = {
        freelancer_name: currentUser?.displayName || 'Freelancer',
        client_name: form.client || '',
        scope_of_work: form.scope || '',
        price: form.price || '',
        currency: form.currency,
        due_date: form.timeline || '',
      }

      const response = await fetch('https://klorah-fast-server-my8l.onrender.com/api/generate-contract', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (data?.contract_text) {
        const db = getFirestore()
        const docRef = await addDoc(collection(db, 'agreements'), {
          title: form.client 
            ? `Agreement with ${form.client}` 
            : 'Untitled Agreement',
          text: data.contract_text,
          status: 'draft',
          createdAt: serverTimestamp(),
          userId: currentUser?.uid || null,
          projectId: form.projectId,
          clientId: form.clientId,
          clientEmail: form.clientEmail, // Store client email for later use
          price: form.price,
          currency: form.currency,
          dueDate: form.timeline ? new Date(form.timeline) : null
        })

        // If this is a project-specific agreement, update the project
        if (form.projectId) {
          const projectRef = doc(db, 'projects', form.projectId)
          await updateDoc(projectRef, {
            agreements: arrayUnion(docRef.id)
          })
        }

        toast.success('Agreement generated successfully!')
        onClose()
        // Navigate directly to the agreement page
        navigate(`/agreement/${docRef.id}`)
      } else {
        toast.error('No contract text returned by AI.')
      }
    } catch (err) {
      console.error(err)
      toast.error('Could not generate AI contract.')
    } finally {
      setIsGeneratingAI(false)
    }
  }

  // Manual Creation - simplified to go directly to Agreement page
  const handleCreateManual = async () => {
    if (!form.manualText.trim()) {
      toast.error('Please enter agreement text')
      return
    }

    setIsGeneratingAI(true) // Reuse loading state
    try {
      const db = getFirestore()
      const docRef = await addDoc(collection(db, 'agreements'), {
        title: form.title?.trim() 
          ? form.title.trim()
          : form.client 
            ? `Agreement with ${form.client}` 
            : 'Untitled Agreement',
        text: form.manualText,
        status: 'draft',
        createdAt: serverTimestamp(),
        userId: currentUser?.uid || null,
        projectId: form.projectId,
        clientId: form.clientId,
        clientEmail: form.clientEmail, // Store client email for later use
        price: form.price,
        currency: form.currency,
        dueDate: form.timeline ? new Date(form.timeline) : null
      })

      // If this is a project-specific agreement, update the project
      if (form.projectId) {
        const projectRef = doc(db, 'projects', form.projectId)
        await updateDoc(projectRef, {
          agreements: arrayUnion(docRef.id)
        })
      }

      toast.success('Agreement created successfully!')
      onClose()
      // Navigate directly to the agreement page
      navigate(`/agreement/${docRef.id}`)
    } catch (err) {
      console.error(err)
      toast.error('Could not create agreement.')
    } finally {
      setIsGeneratingAI(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-gray-500/60 dark:bg-black/60 backdrop-blur-md flex items-center justify-center p-8 fade-in z-[90]">
      {/* Expanded Editor Modal */}
      {showExpandedEditor && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[100]">
          <motion.div
            className="bg-white dark:bg-black rounded-3xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col border border-gray-200 dark:border-[#1A1A1A]"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            {/* Expanded Editor Header */}
            <div className="px-8 py-6 border-b border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-black rounded-t-3xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-medium text-gray-900 dark:text-white">Agreement Editor</h2>
                  <p className="text-sm text-gray-500 dark:text-[#666666] mt-1">Write your agreement with full formatting</p>
                </div>
                <button 
                  onClick={() => setShowExpandedEditor(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-[#1A1A1A] rounded-full transition-colors text-gray-500 dark:text-[#666666] hover:text-gray-700 dark:hover:text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Expanded Editor Body */}
            <div className="px-8 py-6 bg-white dark:bg-black overflow-y-auto flex-1 min-h-0">
              <SimpleRichTextEditor
                content={form.manualText}
                onChange={(text) => setForm(prev => ({ ...prev, manualText: text }))}
                placeholder="Write your agreement here with full formatting capabilities..."
                isExpanded={true}
              />
            </div>

            {/* Expanded Editor Footer */}
            <div className="px-8 py-6 border-t border-gray-200 dark:border-[#1A1A1A] flex justify-end gap-4 bg-white dark:bg-black rounded-b-3xl flex-shrink-0">
              <button
                onClick={() => setShowExpandedEditor(false)}
                className="px-6 py-3 text-sm font-medium bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] rounded-2xl transition-colors text-gray-700 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-[#2A2A2A]"
              >
                Done
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <motion.div
        className="bg-white dark:bg-black rounded-[30px] shadow-2xl w-full max-w-xl max-h-[90vh] flex flex-col overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 20 }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-8 py-6 border-b border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-black rounded-t-[30px]">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-medium text-gray-900 dark:text-white">
              Create Agreement
            </h2>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-[#1A1A1A] rounded-full transition-colors text-gray-500 dark:text-[#666666] hover:text-gray-700 dark:hover:text-white"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6 bg-white dark:bg-black">
          {/* Agreement Mode Selection */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Agreement Type</label>
            <div className="inline-flex items-center bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, mode: 'ai' }))}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  form.mode === 'ai'
                    ? 'bg-white dark:bg-black text-gray-900 dark:text-white shadow-sm border dark:border-gray-700'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                AI Generated
              </button>
              <button
                type="button"
                onClick={() => setForm(prev => ({ ...prev, mode: 'manual' }))}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                  form.mode === 'manual'
                    ? 'bg-white dark:bg-black text-gray-900 dark:text-white shadow-sm border dark:border-gray-700'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
              >
                Manual
              </button>
            </div>
          </div>

          {/* Client - Enhanced with email display and edit capability */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Client Name</label>
            
            {/* Show selected client with email or input mode */}
            {clientCompleted && !isEditingClient && !showNewClientForm ? (
              <div className="relative">
                <div className="bg-gray-50 dark:bg-[#111111] rounded-2xl px-4 py-3 border border-gray-200 dark:border-[#2A2A2A] min-h-[50px] flex items-center justify-between group hover:border-gray-300 dark:hover:border-[#666666] transition-all">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-gray-900 dark:text-white font-medium text-sm truncate">
                          {form.client || 'Client'}
                        </div>
                        {form.clientEmail && (
                          <div className="text-gray-500 dark:text-[#666666] text-xs truncate mt-0.5">
                            {form.clientEmail}
                          </div>
                        )}
                      </div>
                      {clientSaved && (
                        <div className="flex items-center gap-1 text-green-600 dark:text-green-400 flex-shrink-0">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-xs font-medium">Saved</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsEditingClient(true)}
                    className="flex-shrink-0 ml-2 p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-[#1A1A1A] transition-colors opacity-0 group-hover:opacity-100"
                    title="Edit client details"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
              </div>
            ) : (
              <div className="relative" ref={clientDropdownRef}>
                <input
                  type="text"
                  name="client"
                  placeholder="Enter client name"
                  className="w-full bg-gray-50 dark:bg-[#111111] rounded-2xl px-4 py-3 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/10 focus:border-brand text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]"
                  value={form.client}
                  onChange={handleChange}
                  onClick={() => !client && setShowClientDropdown(true)}
                  readOnly={!!client}
                  autoComplete="off"
                />
                {!client && showClientDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-[#111111] rounded-2xl shadow-lg max-h-60 overflow-y-auto border border-gray-200 dark:border-[#2A2A2A]">
                    <CustomerList 
                      customers={customers} 
                      onSelect={handleCustomerSelect} 
                      searchQuery={form.client}
                    />
                  </div>
                )}
              </div>
            )}
            
            {/* Edit Mode for Client */}
            {isEditingClient && (
              <div className="mt-2 bg-white dark:bg-black border border-brand rounded-xl p-4 shadow-lg">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white">Edit Client Details</h4>
                    <button
                      type="button"
                      onClick={() => setIsEditingClient(false)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="space-y-2">
                    <input
                      type="text"
                      placeholder="Client name"
                      value={form.client}
                      onChange={(e) => setForm(prev => ({ ...prev, client: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-[#111111] rounded-lg border border-gray-200 dark:border-[#2A2A2A] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    />
                    <input
                      type="email"
                      placeholder="Client email"
                      value={form.clientEmail}
                      onChange={(e) => setForm(prev => ({ ...prev, clientEmail: e.target.value }))}
                      className="w-full px-3 py-2 bg-gray-50 dark:bg-[#111111] rounded-lg border border-gray-200 dark:border-[#2A2A2A] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingClient(false);
                        setClientSaved(true);
                        // Auto-hide the "Saved" indicator after 3 seconds
                        setTimeout(() => setClientSaved(false), 3000);
                      }}
                      className="flex-1 px-3 py-2 bg-brand hover:bg-brand-hover text-black text-sm font-medium rounded-lg transition-colors"
                    >
                      Save Changes
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingClient(false)}
                      className="px-3 py-2 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] text-gray-700 dark:text-[#666666] text-sm font-medium rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* New Client Form */}
          {showNewClientForm && (
            <div className="p-3 bg-gray-50 dark:bg-[#111111] border border-gray-200 dark:border-[#2A2A2A] rounded-xl">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-gray-500 dark:bg-[#666666] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    Email for "{form.client}"
                  </h4>
                  <p className="text-xs text-gray-600 dark:text-[#666666] mb-2">
                    Will be added to your contacts for future agreements
                  </p>
                  
                  <div className="space-y-2">
                    <input
                      type="email"
                      placeholder="Client's email address"
                      value={newClientEmail}
                      onChange={(e) => setNewClientEmail(e.target.value)}
                      className="w-full px-3 py-2 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-[#2A2A2A] text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand"
                      autoComplete="off"
                    />
                    
                    <div className="flex items-center justify-between">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleAddNewClient}
                          className="px-3 py-1.5 bg-brand hover:bg-brand-hover text-black text-xs font-medium rounded-lg transition-colors"
                        >
                          Continue
                        </button>
                        <button
                          type="button"
                          onClick={handleSkipAddingClient}
                          className="px-3 py-1.5 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] text-gray-700 dark:text-[#666666] text-xs font-medium rounded-lg transition-colors"
                        >
                          Don't Save
                        </button>
                      </div>
                      
                      <button
                        type="button"
                        onClick={() => {
                          setShowNewClientForm(false);
                          setNewClientEmail('');
                        }}
                        className="px-3 py-1.5 bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] text-gray-700 dark:text-[#666666] text-xs font-medium rounded-lg transition-colors"
                      >
                        Skip Email
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Multiple Recipients Manager */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email Recipients</label>
                <p className="text-xs text-gray-500 dark:text-[#666666]">Add up to 5 email addresses to receive this agreement</p>
              </div>
              {form.recipients.length < 5 && (
                <button
                  type="button"
                  onClick={addRecipient}
                  className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-brand hover:text-brand-hover bg-brand/10 hover:bg-brand/20 border border-brand/30 rounded-lg transition-colors"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Add Email
                </button>
              )}
            </div>
            
            <div className="space-y-2">
              {form.recipients.map((recipient, index) => (
                <div key={recipient.id} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-[#0A0A0A] rounded-lg border border-gray-200 dark:border-[#1A1A1A]">
                  <div className="flex-shrink-0 w-5 h-5 bg-brand/20 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-brand">{index + 1}</span>
                  </div>
                  <input
                    type="email"
                    placeholder="recipient@example.com"
                    value={recipient.email}
                    onChange={(e) => updateRecipient(recipient.id, e.target.value)}
                    className="flex-1 px-2 py-1.5 bg-white dark:bg-black rounded-lg border border-gray-200 dark:border-[#2A2A2A] text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666] focus:ring-2 focus:ring-brand/20 focus:border-brand"
                  />
                  {form.recipients.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRecipient(recipient.id)}
                      className="flex-shrink-0 p-1 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
              
              {form.recipients.length === 0 && (
                <div className="text-center py-4 text-gray-500 dark:text-[#666666]">
                  <svg className="w-6 h-6 mx-auto mb-1 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs">No recipients added yet</p>
                  <button
                    type="button"
                    onClick={addRecipient}
                    className="mt-1 text-xs font-medium text-brand hover:text-brand-hover transition-colors"
                  >
                    Add your first recipient
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Manual Text Area - Only show in manual mode */}
          {form.mode === 'manual' && (
            <>
              {/* Title field for manual agreements */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Agreement Title</label>
                <input
                  type="text"
                  name="title"
                  placeholder="Enter agreement title..."
                  className="w-full bg-gray-50 dark:bg-[#111111] rounded-2xl px-4 py-3 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/10 focus:border-brand text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]"
                  value={form.title || ''}
                  onChange={handleChange}
                />
              </div>

              {/* Agreement text area */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Agreement Text</label>
                <SimpleRichTextEditor
                  content={form.manualText}
                  onChange={(text) => setForm(prev => ({ ...prev, manualText: text }))}
                  placeholder="Write or paste your agreement text here..."
                  onExpand={() => setShowExpandedEditor(true)}
                />
              </div>
            </>
          )}

          {/* AI Mode Fields - Only show in AI mode */}
          {form.mode === 'ai' && (
            <>
              {/* Scope of Work */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Scope of Work</label>
                <input
                  type="text"
                  name="scope"
                  placeholder="Brief description of services"
                  className="w-full bg-gray-50 dark:bg-[#111111] rounded-2xl px-4 py-3 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/10 focus:border-brand text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]"
                  value={form.scope}
                  onChange={handleChange}
                  readOnly={!!project}
                />
              </div>

              {/* Timeline */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Timeline</label>
                <input
                  type="date"
                  name="timeline"
                  className="w-full bg-gray-50 dark:bg-[#111111] rounded-2xl px-4 py-3 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/10 focus:border-brand text-gray-900 dark:text-white"
                  value={form.timeline}
                  onChange={handleChange}
                  readOnly={!!project}
                />
              </div>

              {/* Price and Currency */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Price</label>
                  <input
                    type="number"
                    name="price"
                    placeholder="0.00"
                    className="w-full bg-gray-50 dark:bg-[#111111] rounded-2xl px-4 py-3 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/10 focus:border-brand text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]"
                    value={form.price}
                    onChange={handleChange}
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Currency</label>
                  <select
                    name="currency"
                    value={form.currency}
                    onChange={handleChange}
                    className="w-full bg-gray-50 dark:bg-[#111111] rounded-2xl px-4 py-3 border border-gray-200 dark:border-[#2A2A2A] focus:ring-2 focus:ring-brand/10 focus:border-brand text-gray-900 dark:text-white"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                  </select>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-8 py-6 border-t border-gray-200 dark:border-[#1A1A1A] flex justify-end gap-4 bg-white dark:bg-black rounded-b-[30px]">
          <button
            onClick={onClose}
            disabled={isGeneratingAI}
            className="px-6 py-3 text-sm font-medium bg-gray-100 dark:bg-[#1A1A1A] hover:bg-gray-200 dark:hover:bg-[#2A2A2A] rounded-2xl transition-colors text-gray-700 dark:text-[#666666] hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-[#2A2A2A]"
          >
            Cancel
          </button>
          <button
            onClick={form.mode === 'ai' ? handleGenerateAI : handleCreateManual}
            disabled={isGeneratingAI}
            className={`px-6 py-3 text-sm font-medium rounded-2xl transition-colors ${
              isGeneratingAI
                ? 'bg-gray-100 dark:bg-[#1A1A1A] opacity-50 cursor-not-allowed text-gray-500 dark:text-[#666666]'
                : 'bg-brand hover:bg-brand-hover text-black'
            }`}
          >
            {isGeneratingAI ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></div>
                <span>{form.mode === 'ai' ? 'Generating...' : 'Creating...'}</span>
              </div>
            ) : (
              form.mode === 'ai' ? 'Generate Agreement' : 'Create Agreement'
            )}
          </button>
        </div>
      </motion.div>
    </div>
  )
}